import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { downloadOrderExport, fetchAdminOrder, formatOrderNumber } from "@/lib/ordersApi";
import { getApiErrorMessage } from "@/lib/api";
import { useApp } from "@/store/useApp";
import { canSeeOrderPricing, type OrderDetail } from "@/lib/types";
import { ORDER_STATUS_LABELS } from "@/lib/orderUtils";
import { CLIENT_TYPE_LABELS } from "@/lib/clientTypeLabels";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, FileText, Loader2, Package } from "lucide-react";
import { toast } from "sonner";

function fmtCurrency(value: number | undefined | null) {
  if (value == null) return "—";
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtNumber(value: number) {
  return value.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}

function fmtDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR");
}

export default function AdminOrderDetail() {
  const { orderId } = useParams<{ orderId: string }>();
  const { account } = useApp();
  const showPricing = canSeeOrderPricing(account);

  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAdminOrder(orderId)
      .then(data => { if (!cancelled) setOrder(data); })
      .catch(err => { if (!cancelled) setError(getApiErrorMessage(err, "Erro ao carregar o pedido")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orderId]);

  useEffect(() => {
    document.title = order ? `${formatOrderNumber(order.number)} · Pedido` : "Pedido";
  }, [order]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
        <Package className="h-10 w-10 opacity-30" />
        <p className="text-sm">{error ?? "Pedido não encontrado"}</p>
      </div>
    );
  }

  const items = order.items.filter(item => !item.cancelledAt);

  const exportOrder = async (format: "pdf" | "xlsx") => {
    try {
      const { filename, blob } = await downloadOrderExport(order.id, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Pedido exportado (${format.toUpperCase()})`);
    } catch {
      toast.error("Não foi possível exportar o pedido");
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Cabeçalho */}
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">{formatOrderNumber(order.number)}</h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              {order.clientName} · {CLIENT_TYPE_LABELS[order.clientType] ?? order.clientType}
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => void exportOrder("pdf")}>
              <FileText className="h-4 w-4" /> PDF
            </Button>
            <Button size="sm" variant="secondary" onClick={() => void exportOrder("xlsx")}>
              <FileSpreadsheet className="h-4 w-4" /> XLSX
            </Button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <div className="text-xs text-primary-foreground/70">Status</div>
            <div className="text-sm font-medium">{ORDER_STATUS_LABELS[order.status] ?? order.status}</div>
          </div>
          <div>
            <div className="text-xs text-primary-foreground/70">Data de criação</div>
            <div className="text-sm font-medium">{fmtDate(order.createdAt)}</div>
          </div>
          <div>
            <div className="text-xs text-primary-foreground/70">Data de saída</div>
            <div className="text-sm font-medium">{fmtDate(order.expeditionReleasedAt)}</div>
          </div>
          {showPricing && (
            <div>
              <div className="text-xs text-primary-foreground/70">Valor do pedido</div>
              <div className="text-sm font-medium">{fmtCurrency(order.total)}</div>
            </div>
          )}
        </div>
      </div>

      {/* Itens */}
      <div className="rounded-xl border bg-card shadow-sm">
        <div className="flex items-center gap-2 border-b px-4 py-3 text-sm font-medium text-muted-foreground">
          <Package className="h-4 w-4" /> Itens do pedido ({items.length})
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produto</TableHead>
              <TableHead>Corte</TableHead>
              <TableHead>Unidade</TableHead>
              <TableHead className="text-right">Quantidade</TableHead>
              {showPricing && <TableHead className="text-right">Preço unit.</TableHead>}
              {showPricing && <TableHead className="text-right">Subtotal</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(item => {
              const subtotal = item.pricePerUnit != null ? item.quantity * item.pricePerUnit : null;
              return (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">
                    {item.productName}
                    {item.observation ? (
                      <span className="block text-xs text-muted-foreground">{item.observation}</span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{item.cut || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{item.unit}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNumber(item.quantity)}</TableCell>
                  {showPricing && (
                    <TableCell className="text-right tabular-nums">{fmtCurrency(item.pricePerUnit)}</TableCell>
                  )}
                  {showPricing && (
                    <TableCell className="text-right tabular-nums">{fmtCurrency(subtotal)}</TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
